import Tooltip from 'antd/es/tooltip'
import { RcFile } from 'antd/es/upload'
import Modules from 'application/modules'
import { Close, Delete, Tick } from 'assets/svg'
import UploadComponent from 'components/Upload'
import AccessControlledComponent from 'contexts/ACL/ControlledComponent'
import useI18n from 'contexts/I18n/hook'
import { Roles } from 'core/auth/constants'
import { BootstrapBuild } from '../types'
import { formatBytesAsGigabytes, isAcceptedBuildFile, translate } from '../utils'

type BuildConfigurationProps = {
  builds: Array<BootstrapBuild>
  hasSelection: boolean
  referencedBuild?: string
  onDeleteBuild: (build: BootstrapBuild) => void
  onUploadBuild: (file: RcFile) => void
  onCancelUpload: () => void
  uploadingFileName?: string
  uploadProgress: number
}

export default function BuildConfiguration({
  builds,
  hasSelection,
  referencedBuild,
  onDeleteBuild,
  onUploadBuild,
  onCancelUpload,
  uploadingFileName,
  uploadProgress,
}: BuildConfigurationProps) {
  const t = useI18n()

  return (
    <section className="bootstrap-section">
      <div className="bootstrap-section-header">
        <h4 className="bootstrap-section-title">
          {translate(t, 'bootstrap.build_configuration_title')}
        </h4>
        {uploadingFileName && (
          <div className="bootstrap-upload-row">
            <div className="bootstrap-upload-progress">
              <progress className="progress is-primary" value={uploadProgress} max={100} />
              <span>{uploadingFileName}</span>
            </div>
            <Tooltip title={translate(t, 'bootstrap.cancel_upload')}>
              <button
                type="button"
                className="button-link bootstrap-cancel-upload-button"
                onClick={onCancelUpload}
                aria-label={translate(t, 'bootstrap.cancel_upload')}
              >
                <Close className="icon-svg icon-small" />
              </button>
            </Tooltip>
          </div>
        )}

        {hasSelection && !uploadingFileName && (
          <AccessControlledComponent module={Modules.bootstrap} role={Roles.SU}>
            <UploadComponent
              title="bootstrap.build_upload_title"
              subtitle="upload.select_file"
              onConfirm={onUploadBuild}
              onValidation={(file) => isAcceptedBuildFile(file.name)}
            />
          </AccessControlledComponent>
        )}
      </div>

      {!hasSelection && (
        <p className="bootstrap-helper-text">
          {translate(t, 'bootstrap.select_version_platform_for_build')}
        </p>
      )}

      {hasSelection && builds.length === 0 && (
        <p className="bootstrap-helper-text">
          {translate(t, 'bootstrap.no_build_files')}
        </p>
      )}

      {hasSelection && builds.length > 0 && (
        <div className="paper table-container bootstrap-build-table">
          <table className="table is-striped is-fullwidth">
            <thead>
              <tr>
                <th>{translate(t, 'bootstrap.column_filename')}</th>
                <th>{translate(t, 'bootstrap.column_size')}</th>
                <th className="has-text-centered">{translate(t, 'bootstrap.column_referenced_by_init')}</th>
                <th aria-label={translate(t, 'bootstrap.column_actions')} />
              </tr>
            </thead>
            <tbody>
              {builds.map((build) => {
                const isReferenced = build.fileName === referencedBuild

                return (
                  <tr key={build.fileName}>
                    <td>{build.fileName}</td>
                    <td>{formatBytesAsGigabytes(build.fileSizeInBytes)}</td>
                    <td className="has-text-centered">
                      {isReferenced && <Tick className="icon-svg icon-small" />}
                    </td>
                    <td className="has-text-right">
                      <AccessControlledComponent module={Modules.bootstrap} role={Roles.SU}>
                        <button
                          type="button"
                          className="button-link bootstrap-delete-button"
                          disabled={isReferenced}
                          onClick={() => onDeleteBuild(build)}
                          aria-label={translate(t, 'bootstrap.delete_build')}
                          title={
                            isReferenced ? translate(t, 'bootstrap.delete_build_referenced') : undefined
                          }
                        >
                          <Delete className="icon-svg icon-small" />
                        </button>
                      </AccessControlledComponent>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
